import React, {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import mylabsPlusLogo from '@/assets/dashboard/mylabs_plus_logo.png';
import benefit1 from '@/assets/dashboard/membership-benefit-1.png';
import benefit2 from '@/assets/dashboard/membership-benefit-2.png';
import benefit3 from '@/assets/dashboard/membership-benefit-3.png';
import {BioMatchEnrollmentService} from '@/modules/biomatch/services/bioMatchEnrollmentService';
import {type MembershipPlan} from '@/modules/patient-dashboard/constants/membershipPlans';
import {useMembershipPlans} from '@/modules/patient-dashboard/hooks/useMembershipPlans';
import {MEMBERSHIP_FAQ} from '@/modules/patient-dashboard/constants/membershipFaq';
import {useCart} from '@/modules/shop/context/CartContext';
import {membershipLineKey} from '@/modules/shop/types';

const formatUSD = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const MembershipBenefitsView: React.FC = () => {
    const {addLine} = useCart();
    const membershipPlans = useMembershipPlans();
    const {data} = useQuery({
        queryKey: ['biomatch', 'dashboard', 'mine'],
        queryFn: () => BioMatchEnrollmentService.getMyAssembled(),
    });
    const discountPct = data?.mylabs_plus_member_discount_pct ?? null;
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    const onJoin = (plan: MembershipPlan['id']) => {
        const selectedPlan = membershipPlans.find((candidate) => candidate.id === plan);
        if (!selectedPlan) return;

        addLine({
            kind: 'membership',
            key: membershipLineKey(selectedPlan.id),
            plan: selectedPlan.id,
            name: `My Labs Plus — ${selectedPlan.label}`,
            description: 'Exclusive member pricing, customized follow-up testing, and free shipping.',
            image_url: mylabsPlusLogo,
            price_cents: selectedPlan.priceCents,
            member_price_cents: null,
            recurring: selectedPlan.id,
        });
    };

    const dealsCopy =
        discountPct && discountPct > 0
            ? `Members receive a ${discountPct % 1 === 0 ? discountPct : discountPct.toFixed(1)}% Discount on all at-home testing solutions`
            : 'Members receive an exclusive discount on all at-home testing solutions';

    const benefits = [
        {img: benefit1, title: 'Exclusive Member Deals', copy: dealsCopy},
        {
            img: benefit2,
            title: 'Custom Follow-up Kits',
            copy: 'Experience curated testing with personalized panels built for you',
        },
        {
            img: benefit3,
            title: 'Fast & Free Shipping',
            copy: 'More results, less waiting with free expedited shipping on all test kits',
        },
    ];

    return (
        <DashboardLayout>
            {/* Hero */}
            <div className="rounded-3xl bg-gradient-to-br from-mld-primary/10 to-cyan-100 px-6 py-14 text-center">
                <img src={mylabsPlusLogo} alt="My Labs Plus" className="mx-auto mb-6 h-20" />
                <h1 className="text-2xl font-medium text-mld-text-primary">Join Plus and achieve your health goals</h1>
                <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
                    Exclusive member deals, customized follow-up testing, free shipping and more
                </p>
                <p className="mt-6 text-xl font-semibold text-mld-text-primary">
                    {formatUSD(membershipPlans[0].priceCents)}/mo
                </p>
                <p className="text-xs text-gray-500">Cancel Anytime</p>
                <button
                    type="button"
                    onClick={() => onJoin('monthly')}
                    className="mt-4 rounded-full bg-mld-primary px-8 py-3 font-medium text-white transition hover:bg-mld-primary/90">
                    Join My Labs+
                </button>
            </div>

            {/* Benefit cards */}
            <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3">
                {benefits.map((b) => (
                    <div key={b.title} className="text-center">
                        <img
                            src={b.img}
                            alt={b.title}
                            className="mx-auto mb-4 aspect-square w-full max-w-[240px] rounded-2xl object-cover"
                        />
                        <h3 className="text-lg font-semibold text-mld-text-primary">{b.title}</h3>
                        <p className="mx-auto mt-2 max-w-xs text-sm text-gray-500">{b.copy}</p>
                    </div>
                ))}
            </div>

            {/* Plan chooser */}
            <div className="mt-12 rounded-3xl bg-gradient-to-br from-mld-plus-panel-from to-mld-plus-panel-to p-8 text-white md:p-12">
                <h2 className="text-center text-2xl font-semibold">Choose A Plan That Works For You</h2>
                <div className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-6 md:grid-cols-2">
                    {membershipPlans.map((plan) => (
                        <div key={plan.id} className="relative rounded-2xl border border-white/20 p-6 text-center">
                            {plan.savingsDollars != null && (
                                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-mld-primary px-3 py-1 text-xs font-semibold text-mld-plus-panel-to">
                                    SAVE ${plan.savingsDollars}
                                </span>
                            )}
                            <h3 className="text-lg font-semibold">{plan.label}</h3>
                            <p className="mt-3 text-3xl font-semibold text-mld-primary">{formatUSD(plan.priceCents)}</p>
                            <p className="text-xs text-white/70">{plan.cadenceLabel}</p>
                            <button
                                type="button"
                                onClick={() => onJoin(plan.id)}
                                className="mt-5 w-full rounded-full bg-mld-primary px-6 py-3 font-medium text-mld-plus-panel-to transition hover:bg-mld-primary/90">
                                Join My Labs+
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* FAQ */}
            <div className="mt-12">
                <h2 className="text-center text-2xl font-medium text-mld-text-primary">Learn More About My Labs+</h2>
                <div className="mx-auto mt-6 max-w-3xl">
                    {MEMBERSHIP_FAQ.map((item, i) => (
                        <div key={item.q} className="border-b border-mld-panel-border">
                            <button
                                type="button"
                                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                className="flex w-full items-center justify-between py-5 text-left text-mld-text-primary">
                                <span>{item.q}</span>
                                <span className="ml-4 text-gray-400">{openFaq === i ? '▲' : '▼'}</span>
                            </button>
                            {openFaq === i && <p className="pb-5 text-sm text-gray-500">{item.a}</p>}
                        </div>
                    ))}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default MembershipBenefitsView;
